import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  QueryDocumentSnapshot,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { Account } from '@superfunnel/types/account';
import { AssetData, SavedAssetData } from '@superfunnel/types/asset-data';
import { WithId } from '@superfunnel/types/dynamic-page-version';

import { functions } from '../Firebase';
import { emptyConverter } from '../utils';

export const searchImages = async (
  query: string,
  source: 'pexels' | 'unsplash',
  pageSize?: number,
  page?: number,
  orientation?: string,
  color?: string,
  locale?: string,
): Promise<AssetData[]> => {
  const fn = httpsCallable<unknown, AssetData[]>(functions, 'images/search');
  const res = await fn({
    query,
    source,
    pageSize,
    page,
    orientation,
    color,
    locale,
  });

  return res.data;
};

export const markUsed = async (
  source: 'unsplash',
  imageId: string,
): Promise<void> => {
  const fn = httpsCallable<unknown, AssetData[]>(functions, 'images/markUsed');
  await fn({
    source,
    imageId,
  });
};

export const getAssetsColRef = (
  selectedAccount: QueryDocumentSnapshot<Account>,
) =>
  collection(selectedAccount.ref, 'assets').withConverter(
    emptyConverter<SavedAssetData>(),
  );

export const fetchAssets = async (
  selectedAccount: QueryDocumentSnapshot<Account>,
  activeTab: 'Pexels' | 'Unsplash' | string,
  searchQuery?: string,
  page = 0,
  pageSize = 20,
  types: ('image' | 'video')[] = ['image'],
): Promise<WithId<AssetData>[]> => {
  if (activeTab === 'Pexels' || activeTab === 'Unsplash') {
    return searchImages(
      searchQuery || 'Marketing',
      activeTab === 'Pexels' ? 'pexels' : 'unsplash',
      pageSize,
      page || 0,
    );
  } else {
    return fetchLibraryAssets(selectedAccount, searchQuery, page, types);
  }
};

export const fetchLibraryAssets = async (
  selectedAccount: QueryDocumentSnapshot<Account>,
  searchQuery?: string,
  page = 0,
  types: ('image' | 'video')[] = ['image'],
): Promise<SavedAssetData[]> => {
  if (page !== 0) return [];
  return getDocs(
    query(
      getAssetsColRef(selectedAccount),
      where('type', 'in', types),
      orderBy('createdAt', 'desc'),
    ),
  ).then((data) => {
    return data.docs
      .map((d) => {
        return {
          ...d.data(),
          id: d.id,
          source: (d.data().source || 'storage') as any,
        };
      })
      .filter((d) => {
        if (searchQuery && searchQuery.length > 2) {
          return d.alt?.includes(searchQuery);
        }
        return true;
      });
  });
};

export const deleteAssetFromLibrary = async (
  selectedAccount: QueryDocumentSnapshot<Account>,
  assetId: string,
): Promise<void> => {
  const assetRef = doc(getAssetsColRef(selectedAccount), assetId);

  await deleteDoc(assetRef);
};

export const updateAsset = async (
  selectedAccount: QueryDocumentSnapshot<Account>,
  assetId: string,
  data: Pick<SavedAssetData, 'alt'>,
): Promise<void> => {
  const assetRef = doc(getAssetsColRef(selectedAccount), assetId);

  await updateDoc(assetRef, data);
};
